import { Modal, Box, Title, Text, Button, TextInput, FileButton, Flex, SimpleGrid, ScrollArea } from "@mantine/core";
import { IconSparkles, IconWorld, IconUpload, IconTrash } from "@tabler/icons-react";
import { useState, useRef } from "react";
import { useMediaQuery } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { errorNotification } from "../../shared/utils/errorNotification";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

interface CreateProfileModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: (data: any) => void;
  onGeneratingStart?: () => void;
  onGeneratingEnd?: () => void;
}

export default function CreateProfileModal({
  opened,
  onClose,
  onSuccess,
  onGeneratingStart,
  onGeneratingEnd,
}: CreateProfileModalProps) {
  const [companyUrl, setCompanyUrl] = useState("");
  const [brochureFile, setBrochureFile] = useState<File | null>(null);
  const resetRef = useRef<() => void>(null);

  const isMobile = useMediaQuery("(max-width: 576px)");

  const handleBrochureChange = (file: File | null) => {
    const MAX_SIZE = 4 * 1024 * 1024; // 4MB
    if (file && file.size > MAX_SIZE) {
      errorNotification({
        title: "File too large",
        message: "Brochure must be under 4MB. Please use a smaller file or provide a URL instead.",
      });
      resetRef.current?.();
      return;
    }
    setBrochureFile(file);
  };

  const isFormValid = companyUrl.trim() !== "" || brochureFile !== null;

  const handleGenerate = async () => {
    // Close modal immediately and show page-level generating indicator
    onClose();
    onGeneratingStart?.();

    try {
      const formData = new FormData();
      if (companyUrl.trim()) formData.append("url", companyUrl.trim());
      if (brochureFile) formData.append("file", brochureFile);

      const data = await trpc.company.generateProfile.mutate(formData as any);

      if (data.status === "success" && data.data) {
        onSuccess?.(data);
      } else {
        onGeneratingEnd?.();
      }
    } catch (error) {
      handleTrpcError(error);
      onGeneratingEnd?.();
    }
  };

  const features = [
    "Company overview and description",
    "Manufacturing capabilities and services",
    "Product categories and specifications",
    "Geographic coverage and export capabilities",
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={isMobile ? "100%" : "lg"}
      radius={isMobile ? 0 : "lg"}
      withCloseButton={false}
      padding={0}
      fullScreen={isMobile}
    >
      <ScrollArea style={{ flex: 1 }} type="auto">
        <Flex direction="column" p="lg">
          <Flex justify="center" mb="sm">
            <Box
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                backgroundColor: "#EEF2FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconSparkles size={26} color="#2563EB" />
            </Box>
          </Flex>

          <Title order={3} ta="center" mb="xs">
            Create Your Company Profile with GT Setu AI
          </Title>

          <Text ta="center" c="slate.6" fz="sm" mb="lg">
            Provide your company website and/or brochure and GT Setu AI will auto-create your profile for review.
          </Text>

          {/* Website URL */}
          <Box mb="md">
            <Text fz="xs" fw={500} mb={4}>
              Company Website URL
            </Text>
            <TextInput
              placeholder="https://yourcompany.com"
              leftSection={<IconWorld size={16} />}
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
            />
          </Box>
          <Flex align="center" my="sm">
            <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            <Text fz="xs" px="sm">
              AND/OR
            </Text>
            <Box style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
          </Flex>

          {/* PDF Upload */}
          <Box mb="md">
            <Text fz="xs" fw={500} mb={4}>
              Company Brochure (PDF)
            </Text>

            {brochureFile ? (
              <Flex
                align="center"
                justify="space-between"
                p="sm"
                style={{ border: "1px solid #E5E7EB", borderRadius: 8 }}
              >
                <Flex align="center" gap="sm">
                  <IconUpload size={18} color="#2563EB" />
                  <Text fz="sm" style={{ wordBreak: "break-all" }}>
                    {brochureFile.name}
                  </Text>
                </Flex>
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  px={6}
                  leftSection={<IconTrash size={14} />}
                  onClick={() => {
                    setBrochureFile(null);
                    resetRef.current?.();
                  }}
                >
                  Remove
                </Button>
              </Flex>
            ) : (
              <FileButton resetRef={resetRef} onChange={handleBrochureChange} accept="application/pdf">
                {(props) => (
                  <Flex
                    {...props}
                    direction="column"
                    align="center"
                    justify="center"
                    py="lg"
                    style={{
                      border: "2px dashed #E5E7EB",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  >
                    <IconUpload size={24} />
                    <Text fz="sm" mt="xs">
                      Upload PDF
                    </Text>
                    <Text fz="xs" c="slate.5" mt={2}>
                      Max 4MB
                    </Text>
                  </Flex>
                )}
              </FileButton>
            )}
          </Box>

          {/* Info box */}
          <Box p="md" mb="lg" bg="#EEF2FF" style={{ borderRadius: 8 }}>
            <Text fz="xs" fw={500} mb="xs">
              What GT Setu AI Will Generate
            </Text>
            <SimpleGrid cols={1} spacing="xs">
              {features.map((f) => (
                <Text key={f} fz="xs">
                  • {f}
                </Text>
              ))}
            </SimpleGrid>
          </Box>

          {/* Actions */}
          <Button fullWidth disabled={!isFormValid} onClick={handleGenerate}>
            Generate Profile with AI
          </Button>

          <Button fullWidth variant="subtle" mt="xs" onClick={onClose}>
            Skip & Create Manually
          </Button>
        </Flex>
      </ScrollArea>
    </Modal>
  );
}
